import { RuleProperties } from '@react-awesome-query-builder/mui';

import { getElectionsByIds } from '@/apis/elections';
import { isTemplatePlaceholder, TASK_CHOICE_MAPPING, TASK_KIND_MAPPING } from '@/types';
import { TaskFilter } from '@/types/searchFilterTypes';
import { TaskWidgetValue } from '@/pages/AdvancedSearchListCreatorPage/customWidgets/TaskWidget';
import { FilterMapper, ValidationContext } from './filterMapperTypes';
import {
  extractNumericIdsFromGenericEntityArray,
  extractTaskChoiceFromGenericEntityArray,
  extractTaskKindFromGenericEntityArray,
} from './util';

interface TaskRuleProperties extends RuleProperties {
  value: TaskWidgetValue[];
}
// elections, isSinceSpecificElection, kinds, choices, note
export const TaskMapper: FilterMapper = {
  checkIsIncomplete({ value }) {
    const { elections, isSinceSpecificElection } = value[0];
    const isAnElectionSelected = elections.length !== 0;
    const isMoreThanOneSpecificElectionSelected = isSinceSpecificElection && elections.length > 1;
    return !isAnElectionSelected || isMoreThanOneSpecificElectionSelected;
  },

  toFilter({ value }: TaskRuleProperties): TaskFilter {
    if (!value) throw new Error('Missing value');
    if (value.length !== 1) throw new Error('Invalid value. Expected array of length 1');

    // RAQB stores our value in an array of length 1
    const { elections, isSinceSpecificElection, kinds, choices, note } = value[0];

    if (elections === undefined) throw new Error('value[0] missing elections');
    if (isSinceSpecificElection === undefined)
      throw new Error('value[0] missing isSinceSpecificElection');
    if (kinds === undefined) throw new Error('value[0] missing kinds');
    if (choices === undefined) throw new Error('value[0] missing choices');
    return {
      name: 'task',
      value: {
        electionIds: isTemplatePlaceholder(elections)
          ? elections
          : extractNumericIdsFromGenericEntityArray(elections),
        isSinceSpecificElection,
        kinds: isTemplatePlaceholder(kinds) ? kinds : extractTaskKindFromGenericEntityArray(kinds),
        choices: isTemplatePlaceholder(choices)
          ? choices
          : extractTaskChoiceFromGenericEntityArray(choices),
        note: note === '' ? undefined : isTemplatePlaceholder(note) ? note : note,
      },
    };
  },

  async fromFilter(filter: TaskFilter, { token, domainCode }: ValidationContext) {
    const { isSinceSpecificElection, choices, electionIds, kinds, note } = filter.value;

    const elections = isTemplatePlaceholder(electionIds)
      ? electionIds
      : (await getElectionsByIds(token, electionIds, domainCode)).map((election) => ({
          id: election.id,
          label: election.name,
        }));

    const taskChoices = isTemplatePlaceholder(choices)
      ? choices
      : choices.map((choice) => ({
          id: choice,
          label: TASK_CHOICE_MAPPING[choice],
        }));
    const taskKinds = isTemplatePlaceholder(kinds)
      ? kinds
      : kinds.map((kind) => ({ id: kind, label: TASK_KIND_MAPPING[kind] }));

    return {
      field: filter.name,
      operator: 'any in',
      value: [
        {
          isSinceSpecificElection,
          note: note ?? '',
          kinds: taskKinds,
          choices: taskChoices,
          elections,
          errors:
            isTemplatePlaceholder(elections) && isTemplatePlaceholder(electionIds)
              ? []
              : (elections as unknown as unknown[]).length ===
                  (electionIds as unknown as unknown[]).length
                ? []
                : [{ field: 'elections', message: 'Missing elections' }],
        },
      ],
    };
  },
};
